import React, { useState } from 'react';

import TimelineSection from './timeline-section';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { IClassNames, makeClassNames } from '../../utils/styles';
import IconArrowDown from '../icons/icon-arrow-down';
import IconArrowUp from '../icons/icon-arrow-up';
import { AcceptedCookiesConfig } from '../liveblog/liveblog';
import { IPost } from '../post/post';
import './timeline.scss';
import { renderPostComponent } from '../post/renderer';

export interface TimelineProps {
  liveblogUrl?: string;
  posts?: IPost[];
  isPinned?: boolean;
  dateNow: Date;
  newPostsCounter?: number;
  acceptedCookiesConfig?: AcceptedCookiesConfig;
  tenant: LiveblogTenant;
  isWebView?: boolean;
  isLoading?: boolean;
  isSlimTimeline?: boolean;
  handleInfiniteScroll?: Function;
}

const classNames = (
  isPinned: boolean,
  hasReadmore: boolean,
  isReadmoreOpen: boolean,
  isSlimTimeline: boolean,
): IClassNames =>
  makeClassNames({
    Timeline: ['timeline', isSlimTimeline && 'timeline--slim'],
    TimelineSection: 'timeline__section',
    TimelineWrapper: 'timeline__aside',
    TimelineDot: `timeline__dot`,
    TimelineDotActive: [
      !isPinned && `timeline__dot--active`,
      isPinned && 'timeline__dot--active--pinned',
    ],
    TimelineLine: 'timeline__line',
    TimelineMain: 'timeline__main',
    TimelineReadmoreContainer: [
      hasReadmore && 'timeline__readmore__container',
      hasReadmore && !isReadmoreOpen && 'readmore_closed',
    ],
    TimelineReadMoreAction: 'timeline__readmore__action',
    TimelineContentWrapper: 'timeline__content-wrapper',
    TimelineAdv: 'timeline__adv',
    TimelineAdvLabel: 'timeline__adv-label',
    TimelineSectionHidden: 'timeline__section-hidden',
  });

const Timeline: React.FC<TimelineProps> = ({
  liveblogUrl,
  posts = [] /* istanbul ignore next: default value */,
  isPinned = false,
  dateNow,
  newPostsCounter = 0,
  acceptedCookiesConfig,
  tenant,
  isWebView,
  isSlimTimeline = false,
  handleInfiniteScroll,
}) => {
  const hasReadmore = isPinned;
  const [isReadmoreOpen, setReadmoreOpen] = useState<boolean>(false);

  const cn = classNames(isPinned, hasReadmore, isReadmoreOpen, isSlimTimeline);

  const readMoreClick = () => {
    if (isReadmoreOpen) {
      setReadmoreOpen(false);
    } else {
      setReadmoreOpen(true);
    }
  };

  return (
    <div className={cn.Timeline}>
      <div
        role="list"
        className={cn.TimelineReadmoreContainer}
        style={isReadmoreOpen ? { height: 'auto' } : {}}
      >
        {posts.map((post, index) => (
          <TimelineSection
            key={post.postId}
            postId={post.postId}
            tenant={tenant}
            cn={cn}
            index={index}
            isObserved={post.isObserved}
            isHidden={post.isHidden}
            newPostsCounter={newPostsCounter}
            isSlimTimeline={isSlimTimeline}
            isWebView={isWebView}
            handleInfiniteScroll={handleInfiniteScroll}
            pageNumber={post.pageNumber || 1}
          >
            {renderPostComponent({
              liveblogUrl,
              post,
              hasReadmore,
              dateNow,
              acceptedCookiesConfig,
              tenant,
              isSlimTimeline,
              isWebView,
            })}
          </TimelineSection>
        ))}
      </div>

      {hasReadmore && (
        <button
          className={cn.TimelineReadMoreAction}
          type="button"
          onClick={readMoreClick}
        >
          {isReadmoreOpen ? (
            <>
              Leggi di meno <IconArrowUp width={10} />
            </>
          ) : (
            <>
              Leggi di più <IconArrowDown width={10} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default Timeline;
